import {createContext, ReactNode, useCallback, useMemo, useState} from "react";

import {dynamicTabConfig, staticTabConfig} from "@/components/layout/TabContentContainer.types.ts";
import {ComponentStatus, IDPInfo, IncidentType, StatusPage} from "@/lib/types/statuspages.ts";
import {StatusPagePreviewOverrides} from "@/pages/statuspages/shared/livePreview.ts";
import {StatusPageManageTabs} from "@/pages/statuspages/statuspages-manage/ManageStatusPageTabContainer.types.ts";
import {StatusPageComponentsTab} from "@/pages/statuspages/tabs/components/StatusPageComponentsTab.tsx";
import {StatusPageManageDesignTabWrapper} from "@/pages/statuspages/tabs/design/StatusPageManageDesignTabWrapper.tsx";
import {StatusPageExternalUsersTab} from "@/pages/statuspages/tabs/external-users/StatusPageExternalUsersTab.tsx";
import {ManageStatusPageGeneralTab} from "@/pages/statuspages/tabs/general/ManageStatusPageGeneralTab.tsx";
import {StatusPageIncidentsTab} from "@/pages/statuspages/tabs/incidents/StatusPageIncidentsTab.tsx";
import {StatusPageSSOTab} from "@/pages/statuspages/tabs/sso/StatusPageSSOTab.tsx";
import {StatusPageSubscribersTab} from "@/pages/statuspages/tabs/subscribers/StatusPageSubscribersTab.tsx";
import {StatusPageSystemMetricsTab} from "@/pages/statuspages/tabs/systemMetrics/StatusPageSystemMetricsTab.tsx";

export const StaticStatusPageManageTabConfig: Record<StatusPageManageTabs, staticTabConfig<void, StatusPage>> = {
  general: {
    id: "general",
    title: "General",
    formTab: true,
    html: () => <ManageStatusPageGeneralTab />,
  },
  design: {
    id: "design",
    title: "Design",
    formTab: true,
    html: () => <StatusPageManageDesignTabWrapper />,
  },
  components: {
    id: "components",
    title: "Components",
    html: () => <StatusPageComponentsTab />,
    getBadgeText: (statusPage: StatusPage) =>
      statusPage.components
        .reduce(
          (componentCount, component) => componentCount + (component.is_group ? component.subcomponents.length : 1),
          0
        )
        .toString(),
  },
  [StatusPageManageTabs.SYSTEM_METRICS]: {
    id: "system-metrics",
    title: "System Metrics",
    html: () => <StatusPageSystemMetricsTab />,
    getBadgeText: (statusPage: StatusPage) => statusPage.metrics.length.toString(),
  },
  incidents: {
    id: "incidents",
    title: "Incidents",
    html: () => <StatusPageIncidentsTab key={IncidentType.INCIDENT} incidentType={IncidentType.INCIDENT} />,
    getBadgeText: (statusPage: StatusPage) =>
      statusPage.active_incidents.filter((i) => i.incident_type === IncidentType.INCIDENT).length.toString(),
  },
  maintenance: {
    id: "maintenance",
    title: "Maintenance",
    html: () => (
      <StatusPageIncidentsTab
        key={IncidentType.SCHEDULED_MAINTENANCE}
        incidentType={IncidentType.SCHEDULED_MAINTENANCE}
      />
    ),
    getBadgeText: (statusPage: StatusPage) =>
      statusPage.active_incidents
        .filter((i) => i.incident_type === IncidentType.SCHEDULED_MAINTENANCE)
        .length.toString(),
  },
  subscribers: {
    id: "subscribers",
    title: "Subscribers",
    html: () => <StatusPageSubscribersTab />,
    getBadgeText: (statusPage: StatusPage) => statusPage.subscribers_count.toString(),
  },
  sso: {
    id: "sso",
    title: "SSO",
    formTab: true,
    html: () => <StatusPageSSOTab />,
    getBadgeText: (statusPage: StatusPage) =>
      statusPage.samlSettings.idp_entity_id.length > 0 || statusPage.samlSettings.idp_sso_target_url.length > 0
        ? "1"
        : "0",
  },
  [StatusPageManageTabs.EXTERNAL_USERS]: {
    id: "external-users",
    title: "External Users",
    html: () => <StatusPageExternalUsersTab />,
    getBadgeText: (statusPage: StatusPage) => statusPage.external_users_count.toString(),
  },
};

export const defaultStatusPageManageConfig: Record<StatusPageManageTabs, dynamicTabConfig> = {
  general: {
    isVisible: true,
  },
  design: {
    isVisible: true,
  },
  components: {
    isVisible: true,
  },
  [StatusPageManageTabs.SYSTEM_METRICS]: {
    isVisible: true,
  },
  incidents: {
    isVisible: true,
  },
  maintenance: {
    isVisible: true,
  },
  subscribers: {
    isVisible: true,
  },
  sso: {
    isVisible: false,
  },
  [StatusPageManageTabs.EXTERNAL_USERS]: {
    isVisible: false,
  },
};

// Static manage-page data. Stable for the lifetime of the page (only changes when the status page
// is refetched), so consumers do not re-render when the live preview updates.
interface StatusPageContext {
  statusPage: StatusPage;
  componentStatuses: ComponentStatus[];
  testIDPs: {
    items: IDPInfo[];
  };
}

const StatusPageContextDefault: StatusPageContext = {
  statusPage: {} as StatusPage,
  componentStatuses: [] as ComponentStatus[],
  testIDPs: {
    items: [] as IDPInfo[],
  },
};

export const StatusPageContext = createContext<StatusPageContext>(StatusPageContextDefault);

// Live-preview write actions. The value is stable, so components that only push preview updates
// (e.g. the edit form) never re-render when the preview state itself changes.
interface StatusPagePreviewActionsContext {
  // Bumped to force the live preview iframe to reload (e.g. after a successful save)
  reloadPreview: () => void;
  // Pushes the latest unsaved form values (mapped onto the consumption page) to the preview iframe
  setPreviewOverrides: (overrides: StatusPagePreviewOverrides | null) => void;
}

const StatusPagePreviewActionsContextDefault: StatusPagePreviewActionsContext = {
  reloadPreview: () => {},
  setPreviewOverrides: () => {},
};

export const StatusPagePreviewActionsContext = createContext<StatusPagePreviewActionsContext>(
  StatusPagePreviewActionsContextDefault
);

// Live-preview state. Changes on every debounced form edit, so only the preview surfaces consume it
// and re-render in response; the rest of the manage page is isolated from these updates.
interface StatusPagePreviewStateContext {
  previewReloadKey: number;
  previewOverrides: StatusPagePreviewOverrides | null;
}

const StatusPagePreviewStateContextDefault: StatusPagePreviewStateContext = {
  previewReloadKey: 0,
  previewOverrides: null,
};

export const StatusPagePreviewStateContext = createContext<StatusPagePreviewStateContext>(
  StatusPagePreviewStateContextDefault
);

interface StatusPageProviderProps {
  statusPage: StatusPage;
  componentStatuses: ComponentStatus[];
  testIDPs: {
    items: IDPInfo[];
  };
  children: ReactNode;
}

export const StatusPageProvider = ({children, statusPage, componentStatuses, testIDPs}: StatusPageProviderProps) => {
  const [previewReloadKey, setPreviewReloadKey] = useState(0);
  const [previewOverrides, setPreviewOverrides] = useState<StatusPagePreviewOverrides | null>(null);
  const reloadPreview = useCallback(() => setPreviewReloadKey((key) => key + 1), []);

  const staticValue = useMemo(
    () => ({statusPage, componentStatuses, testIDPs}),
    [statusPage, componentStatuses, testIDPs]
  );
  const previewActions = useMemo(() => ({reloadPreview, setPreviewOverrides}), [reloadPreview]);
  const previewState = useMemo(() => ({previewReloadKey, previewOverrides}), [previewReloadKey, previewOverrides]);

  return (
    <StatusPageContext.Provider value={staticValue}>
      <StatusPagePreviewActionsContext.Provider value={previewActions}>
        <StatusPagePreviewStateContext.Provider value={previewState}>{children}</StatusPagePreviewStateContext.Provider>
      </StatusPagePreviewActionsContext.Provider>
    </StatusPageContext.Provider>
  );
};
